/**
 * @file user.schema.ts
 * @description Este arquivo define o schema Zod para a entidade principal 'User'.
 * Ele utiliza uma união discriminada (discriminated union) baseada no campo 'role'
 * para lidar com os diferentes tipos de usuários do sistema (ex: PregnantWoman, Guest).
 *
 * Este schema é a fonte única da verdade para a validação e tipagem de todos
 * os objetos de usuário na aplicação.
 */

import { z } from 'zod';
import {
  FeedbackSummarySchema,
  OccupationEnum,
  ProfessionalIdSchema,
  ProviderTypeEnum,
} from './medical-provider.schema';
import { LocationSchema } from './location.schema';

// --- Helper Schemas & Enums ---
// Schemas e enums reutilizáveis que definem partes menores do modelo principal.

/**
 * Define os papéis (roles) possíveis para um usuário no sistema.
 */
const UserRoles = z.enum(['PREGNANT_WOMAN', 'GUEST', 'INTERNAL', 'PROVIDER']);

/**
 * Define os tipos de usuário para gestantes (contexto de conteúdo).
 */
const UserTypeEnum = z.enum(['PREGNANT', 'TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE', 'FATHER_FIGURE']);

/**
 * Define o sub-tipo de figura paterna (para qual perfil é acompanhante).
 */
const FatherFigureForEnum = z.enum(['PREGNANT', 'TENTANTE', 'POSTPARTUM', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE']);

/**
 * Define os presets de permissões para usuários internos.
 */
// COMPANY_MANAGER entrou com a camada de empresa (fase 4 do ecossistema de
// marcas). Alargar o enum é seguro — restringir é que não seria: documento
// gravado com um valor que saiu do enum derrubaria a listagem inteira.
const PermissionsPresets = z.enum(['CRM', 'CMS', 'DEV', 'SUDO', 'PARTNER_ADMIN', 'PARTNER_CRM', 'PARTNER_CMS', 'COMPANY_MANAGER']);

/**
 * Define a estrutura da paridade (histórico obstétrico),
 * incluindo um campo de texto opcional para comentar sobre perdas gestacionais.
 */
const ParitySchema = z.object({
  pregnancies: z.number().int(), // G
  vaginalBirths: z.number().int().default(0), // V
  cesareanBirths: z.number().int().default(0), // C
  // Deprecado, substituído por `cesareanBirths`. Guardava o número de CESÁREAS:
  // nasceu com esse nome em 2025-06-30 e o formulário que o preenchia sempre o
  // rotulou "Cesáreas". Um comentário anterior aqui dizia que era o total de
  // partos (P) — provável leitura de `{pregnancies, cSections, losses}` como se
  // fosse a tríade G-P-A. Não é, e nenhum documento de produção tem valor aqui.
  cSections: z.number().int().optional(),
  losses: z.number().int(), // A
  commentLosses: z.string().nullable().optional(),
});

/**
 * Consentimento de marketing por canal (LGPD).
 * Pré-requisito para sincronizar/comunicar contatos com plataformas externas
 * (ex.: Brevo). Apenas contatos com consentimento explícito podem receber
 * comunicações de marketing; webhooks de unsubscribe revogam o canal.
 */
const MarketingConsentSchema = z.object({
  // Opt-in por canal
  email: z.boolean().default(false),
  sms: z.boolean().default(false),
  webpush: z.boolean().default(false),
  // Auditoria do consentimento
  updatedAt: z.coerce.date().nullable().optional(),
  // Origem do opt-in/opt-out (ex.: 'signup', 'profile', 'brevo-webhook')
  source: z.string().optional(),
});


// --- Base Schema for All Users ---
// Define o conjunto mínimo de campos comuns a todos os papéis de usuário.

const BaseUserSchema = z.object({
  /**
   * O retrato — de qualquer conta (A034).
   *
   * Estava declarado só em `PregnantWomanSchema`. Consequência: o documento da
   * profissional recebia a URL do upload e o Zod a DESCARTAVA na leitura —
   * gravado no banco, invisível para o app, sem erro em lugar nenhum. Pego na
   * verificação de 10/09/2026: upload 200, perfil sem retrato.
   *
   * Toda conta tem rosto, e o da profissional é o que a paciente vê na
   * conversa.
   */
  avatarUrl: z.string().nullable().optional(),

  /*
   * O perfil profissional mora na BASE, e não no papel `PROVIDER` (A034).
   *
   * Ele começou dentro de `ProviderUserSchema`, o que embutia uma regra sem
   * querer: para ter perfil profissional, a conta precisava SER um documento
   * de profissional. Aí uma paciente convidada para atender ficava sem saída —
   * virar `PROVIDER` apagaria a gestação dela.
   *
   * E os campos não conflitam: bio, ocupação e registro não brigam com
   * gestação nem com preset de equipe. Quem proibia era a união discriminada,
   * não o dado. Aqui a regra fica sendo a certa: profissional é quem tem o
   * VÍNCULO; o documento só guarda o que a paciente vê.
   */
  /** Prefixo do nome exibido à paciente (ex.: "Dra.", "Enf."). */
  prefix: z.string().nullable().optional(),
  /** Descrição exibida no card de agendamento. */
  bio: z.string().optional(),
  /** Registros profissionais (CRM, RQE…). */
  professionalIds: z.array(ProfessionalIdSchema).optional(),
  occupation: OccupationEnum.optional(),
  /** Referências à coleção `specialties`. */
  specialtyIds: z.array(z.string()).optional(),
  /** Locais de atendimento. */
  locations: z.array(LocationSchema).optional(),
  /** Agregado dos feedbacks. */
  feedbackSummary: FeedbackSummarySchema.nullable().optional(),
  /**
   * Continua aqui por compatibilidade de leitura. A afiliação de marca é
   * VÍNCULO (`memberships`, papel `PROVIDER`) desde A033 — foi a mesma
   * lição do papel da conta e do escopo da paciente: relação com marca não
   * cabe em campo do documento.
   */
  type: ProviderTypeEnum.optional(),

  // ID único do usuário
  id: z.string(),

  // Dados essenciais de identificação
  email: z.string(),
  fullName: z.string(),

  /**
   * Marcas em que a conta tem vínculo `PATIENT` ativo (A052, fase 3).
   *
   * Desnormalizado de `memberships` pelo `MembershipService` a cada escrita
   * de vínculo, e conferido pelo script de paridade. Existe por um motivo
   * só: o CRM lista e busca pacientes com paginação e ordenação, e juntar
   * `memberships` com `users` a cada página não é viável no Firestore.
   * **Nunca é fonte de verdade** — quem decide acesso é o vínculo.
   */
  marcas: z.array(z.string()).optional(),

  // Hash da senha do usuário (bcrypt)
  passwordHash: z.string(),

  // Campo para controlar o status de verificação do email
  emailVerified: z.boolean(),

  // Campos de auditoria e exclusão lógica (soft delete).
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),

  // Nome preferido do usuário (social ou apelido), opcional e nullable para compatibilidade.
  preferredName: z.string().nullable().optional(),

  // Se true, obriga o usuário a trocar a senha no próximo login
  forcePasswordChange: z.boolean().default(false),
});

// --- External User Schema ---
// Define campos adicionais para usuários externos (clientes do sistema).

const ExternalUserSchema = BaseUserSchema.extend({
  // Dados de contato e identificação específicos para usuários externos
  telefone: z.string().optional(),
  preferredName: z.string(),
  cpf: z.string(),

  // ID do parceiro B2B ao qual o usuário está associado.
  // Transforma string vazia em null automaticamente
  partnerId: z.string().nullable().optional().transform(val => val === '' ? null : val),

  // Empresa da carteira da marca, quando a pessoa entrou como colaboradora.
  //
  // Colaboradora NÃO é um novo papel: é esta mesma paciente, com o vínculo
  // adicional a uma empresa. Modelá-la como quarto role duplicaria prontuário,
  // jornada e conteúdo. Nasce opcional — nenhum documento existente muda.
  companyId: z.string().nullable().optional().transform(val => val === '' ? null : val),

  // Controle de acesso a conteúdos específicos (exceções, modo whitelist).
  // Aceita o campo legado `allowedCourseIds` via preprocess para compatibilidade
  // com docs ainda não migrados.
  contentAccess: z.preprocess(
    (raw) => {
      if (typeof raw !== 'object' || raw === null) return raw;
      const v = raw as Record<string, unknown>;
      return {
        ...v,
        allowedVideoIds: v.allowedVideoIds ?? v.allowedCourseIds,
      };
    },
    z.object({
      allowedVideoIds: z.array(z.string()).default([]),
    })
  ).optional(),

  // Data/hora do último login bem-sucedido (alimenta o status do Controle de Acesso).
  lastLoginAt: z.coerce.date().nullable().optional(),

  // Consentimento de marketing por canal (LGPD). Ausente = sem opt-in.
  marketingConsent: MarketingConsentSchema.optional(),
});


// --- Role-Specific Schemas ---
// Schemas que estendem os modelos base com campos específicos para cada papel.

/**
 * Estende o schema de usuário externo com campos aplicáveis apenas a uma 'PregnantWoman'.
 */
const PregnantWomanSchema = ExternalUserSchema.extend({
  role: z.literal('PREGNANT_WOMAN'),
  currentPregnancyId: z.string().nullable().optional(),
  userType: UserTypeEnum.optional().default('PREGNANT'),
  fatherFigureFor: FatherFigureForEnum.optional(),
  dateOfBirth: z.date().optional(),
  city: z.string().optional(),
  uf: z.string().optional(),
  /**
   * A empresa onde a pessoa diz trabalhar, perguntada no onboarding quando a
   * marca pede (A097). Declarado, sem prova: não é o vínculo com empresa
   * (`companyId` do vínculo), que só nasce por link, domínio ou convite.
   */
  empresaOndeTrabalha: z.string().nullable().optional(),
  parity: ParitySchema.optional(),
  providerId: z.string().nullable().optional(),

  /**
   * A conta existe, mas o onboarding não terminou (A062, fase C).
   *
   * No cadastro novo a conta nasce antes do onboarding: nome, e-mail, senha
   * e código, e só depois a escolha da jornada. Até lá não há momento, e o
   * `userType` acima cairia no padrão `PREGNANT` — a pessoa seria lida como
   * gestante. Enquanto este campo for `true`, o app leva ao onboarding em vez
   * da home, e o `userType` não deve ser lido como escolha dela.
   *
   * Ausente = concluído: é o caso de toda conta anterior a isto.
   */
  onboardingPendente: z.boolean().optional(),

  /**
   * Como a pessoa quer ser tratada (A062, fase E). Perguntado a quem entra
   * para acompanhar a jornada de alguém — nem toda família tem uma "figura
   * paterna". Ausente = não informado.
   */
  genero: z.enum(['FEMININO', 'MASCULINO', 'OUTRO', 'NAO_INFORMADO']).optional(),

  /**
   * Quem esta conta acompanha (A062, fase E), quando entrou pelo convite de
   * acesso compartilhado. Na gestação o acesso é a gestação (`sharedWith`);
   * nas outras jornadas é este registro — que não dá acesso a dado nenhum.
   */
  acompanhaUserId: z.string().nullable().optional(),

  /**
   * Meses (`AAAA-MM`) em que a conta acessou o app (A062, fase F). Era o
   * "ativa no mês" do painel de uso, retirado em 06/10/2026; nada mais grava
   * nem lê. Fica opcional para as contas de QA que já o têm.
   */
  mesesComAcesso: z.array(z.string()).optional(),

  /**
   * Aceite dos termos de uso e da política de privacidade (A062): versão do
   * texto aceito e quando. Ausente em conta anterior ao aceite gravado.
   */
  termosAceitos: z
    .object({ versao: z.string().min(1), em: z.coerce.date() })
    .optional(),
});

/**
 * Estende o schema de usuário externo para um 'Guest'. Não possui campos extras além dos externos.
 */
const GuestSchema = ExternalUserSchema.extend({
  role: z.literal('GUEST'),
});

/**
 * Estende o schema base para usuários internos (funcionários) com sistema de permissões.
 */
const InternalUserSchema = BaseUserSchema.extend({
  role: z.literal('INTERNAL'),
  permissionsPresets: z.array(PermissionsPresets),
  partnerId: z.string().nullable().optional().transform(val => val === '' ? null : val),
});

const ProviderUserSchema = ExternalUserSchema.extend({
  role: z.literal('PROVIDER'),

  /**
   * O perfil público, que até A033 vivia em `medicalProviders`.
   *
   * A separação existia porque a relação era um-para-muitos: um laboratório
   * tinha um cadastro no catálogo e várias pessoas com login. Depois que
   * `LABORATORY` e `IMAGING_CLINIC` saíram do enum e tudo virou profissional
   * individual, o caso deixou de existir — medido em produção em 10/09/2026:
   * nenhum perfil com mais de uma conta em uso, nenhum profissional em mais
   * de uma marca, e nenhuma tela de paciente que liste o catálogo.
   *
   * Os campos nascem OPCIONAIS: é a fase expand. Nada os lê ainda, e conta de
   * profissional gravada antes do backfill continua válida.
   */
  /**
   * OPCIONAL desde A034.
   *
   * Era obrigatório porque a conta só existia atrelada a um documento de
   * `medicalProviders`. Com o perfil dentro da conta, profissional nova nasce
   * sem perfil separado — o campo sobrevive apenas para as contas antigas,
   * enquanto a coleção existir, e sai junto com ela no contract.
   */
  medicalProviderId: z.string().optional(),

});


// --- Main User Schema ---
// Define o schema principal e completo para a entidade User usando uma união discriminada.

/**
 * O schema final para um usuário. Ele selecionará o schema a ser usado
 * (PregnantWomanSchema, GuestSchema ou InternalUserSchema) com base no valor do campo 'role'.
 */
const UserSchema = z.discriminatedUnion('role', [
  PregnantWomanSchema,
  GuestSchema,
  InternalUserSchema,
  ProviderUserSchema,
]);


// --- Inferred TypeScript Types ---
// Tipos derivados dos schemas para garantir a segurança de tipos (type safety) na aplicação.

type UserRole = z.infer<typeof UserRoles>;
type UserType = z.infer<typeof UserTypeEnum>;
type FatherFigureFor = z.infer<typeof FatherFigureForEnum>;
type PermissionsPreset = z.infer<typeof PermissionsPresets>;
type MarketingConsent = z.infer<typeof MarketingConsentSchema>;
type User = z.infer<typeof UserSchema>;


// --- Public Exports ---
// Exporta os schemas e tipos para serem utilizados em outras partes do sistema.

export { UserRoles, UserTypeEnum, FatherFigureForEnum, PermissionsPresets, MarketingConsentSchema, UserSchema, BaseUserSchema, ExternalUserSchema, InternalUserSchema, ProviderUserSchema };

export type { UserRole, UserType, FatherFigureFor, PermissionsPreset, MarketingConsent, User };